import React, { useEffect } from 'react';
import styles from './CampoMultiSeletor.module.css';
import Icon from '@mdi/react';
import {
  mdiCheck,
  mdiChevronDown,
  mdiDeleteCircleOutline,
  mdiMagnify,
} from '@mdi/js';
import CampoTexto from './CampoTexto';
import ReactDOMServer from 'react-dom/server';
import HBox from '../layout/HBox';
import { Select } from '@base-ui/react/select';

export type CampoMultiSeletorOption = {
  id: string;
  label: React.ReactNode;
  group?: string;
  error?: boolean;
};

type CampoMultiSeletorProps = {
  value?: string[];
  onChange?: (value: string[]) => void;
  label?: string;
  placeholder?: string;
  type?: string;
  required?: boolean;
  options: CampoMultiSeletorOption[];
  disabled?: boolean;
  error?: string;
  noSearch?: boolean;
  instanceId?: string;
  [key: string]: any;
};

export default function CampoMultiSeletor({
  value,
  onChange,
  label,
  placeholder,
  type = 'text',
  required,
  options,
  disabled,
  error,
  noSearch,
  instanceId,
  ...props
}: Readonly<CampoMultiSeletorProps>) {
  const [search, setSearch] = React.useState<string>('');

  const filteredOptions =
    search.length > 0
      ? options?.filter(
        it =>
          !it.label ||
          ReactDOMServer.renderToString(it.label)
            .toLowerCase()
            .includes(search.toLowerCase()),
      )
      : options;

  const groups = filteredOptions?.reduce<{
    [key: string]: CampoMultiSeletorOption[];
  }>((acc, it) => {
    if (it.group) {
      acc[it.group] = acc[it.group] ?? [];
      acc[it.group].push(it);
    } else {
      acc[''] = acc[''] ?? [];
      acc[''].push(it);
    }
    return acc;
  }, {});

  console.log('options', options);
  console.log('filteredOptions', filteredOptions);
  console.log('groups', groups);

  const renderValue = (selectedValues: string[]) => {
    if (!selectedValues || selectedValues.length === 0) {
      return null;
    }

    return (
      <HBox>
        {selectedValues.map((selectedId) => {
          const option = options.find(opt => opt.id === selectedId);
          if (!option) return null;
          
          return (
            <div key={selectedId} className={styles.chip}>
              <div className={styles.chipLabel}>
                {option.label}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const newValue = selectedValues.filter(id => id !== selectedId);
                    onChange?.(newValue);
                  }}
                  type="button"
                  disabled={disabled}
                >
                  <Icon path={mdiDeleteCircleOutline} size="16px" />
                </button>
              </div>
            </div>
          );
        })}
      </HBox>
    );
  };

  return (
    <div style={{ width: '100%', position: 'relative', overflow: 'visible' }}>
      <label>{label}</label>
      <Select.Root
        multiple
        value={value ?? []}
        onValueChange={(newValue) => onChange?.(newValue as string[])}
        disabled={disabled}
        modal={false}
      >
        <Select.Trigger
          id={instanceId}
          className={[
            styles.container,
            error ? styles.error : null,
            disabled ? styles.disabled : null,
          ].join(' ')}
        >
          {placeholder && (!value || value.length === 0) && (
            <div className={styles.placeholder}>{placeholder}</div>
          )}
          <div className={styles.selectedContainer}>
            <Select.Value>
              {renderValue}
            </Select.Value>
          </div>
          <Select.Icon className={styles.arrow}>
            <Icon path={mdiChevronDown} size={1} />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Positioner
            className={styles.positioner}
            alignItemWithTrigger={false}
            side="bottom"
            align="start"
            sideOffset={4}
          >
            <Select.Popup className={styles.options}>
              {!noSearch && (
                <div style={{ padding: '0 0 8px 0' }}>
                  <CampoTexto
                    prepend={
                      <Icon path={mdiMagnify} size={1} color="var(--tc-color-gray-400)" />
                    }
                    placeholder="Pesquisar"
                    value={search}
                    onChange={setSearch}
                  />
                </div>
              )}
              {search.length > 0 && !filteredOptions?.length ? (
                <div className={styles.noResults}>(Nenhum resultado encontrado)</div>
              ) : (
                groups && Object.entries(groups).map(([group, gOptions]) => (
                  <React.Fragment key={group || '__no_group__'}>
                    {group && <div className={styles.group}>{group}</div>}
                    {gOptions.map((option) => (
                      <Select.Item
                        key={option.id}
                        value={option.id}
                        className={[
                          styles.option,
                          group ? styles.grouped : null,
                          option.error ? styles.error : null,
                        ].join(' ')}
                      >
                        <span style={{ minWidth: '32px', marginRight: '4px' }}>
                          <Select.ItemIndicator>
                            <Icon
                              path={mdiCheck}
                              size="14px"
                              color="var(--tc-color-primary)"
                            />
                          </Select.ItemIndicator>
                        </span>
                        <Select.ItemText>{option.label}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </React.Fragment>
                ))
              )}
            </Select.Popup>
          </Select.Positioner>
        </Select.Portal>
      </Select.Root>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}
